/**
 * Granular Element Components
 * Building-block elements for composing page sections from scratch.
 * Each component is minimal and designed to be used via Puck's Drawer drag-and-drop.
 */

import * as React from 'react'

import { sanitizeHtmlValue } from '@/lib/element-schema-sanitizers'
import { cn } from '@/lib/utils'

// ─── Layout Elements ─────────────────────────────────────────────────

export interface ElementSectionProps {
  /** Background color class */
  bgColor?: string
  /** Padding class */
  padding?: string
  /** Additional CSS classes */
  className?: string
  children?: React.ReactNode
}

export function ElementSection({ bgColor, padding = 'py-16 px-4', className, children }: ElementSectionProps) {
  return <section className={cn('w-full', bgColor, padding, className)}>{children}</section>
}

export interface ElementContainerProps {
  /** Max width class */
  maxWidth?: string
  /** Padding class */
  padding?: string
  className?: string
  children?: React.ReactNode
}

export function ElementContainer({
  maxWidth = 'max-w-7xl',
  padding = 'px-4',
  className,
  children,
}: ElementContainerProps) {
  return <div className={cn('mx-auto w-full', maxWidth, padding, className)}>{children}</div>
}

export interface ElementStackProps {
  /** Gap class */
  gap?: string
  /** Alignment */
  align?: 'start' | 'center' | 'end' | 'stretch'
  className?: string
  children?: React.ReactNode
}

export function ElementStack({ gap = 'gap-4', align = 'stretch', className, children }: ElementStackProps) {
  const alignMap: Record<string, string> = {
    start: 'items-start',
    center: 'items-center',
    end: 'items-end',
    stretch: 'items-stretch',
  }
  return <div className={cn('flex flex-col', gap, alignMap[align], className)}>{children}</div>
}

export interface ElementGridProps {
  /** Number of columns */
  columns?: number
  /** Gap class */
  gap?: string
  className?: string
  children?: React.ReactNode
}

export function ElementGrid({ columns = 3, gap = 'gap-6', className, children }: ElementGridProps) {
  return (
    <div className={cn('grid', gap, className)} style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
      {children}
    </div>
  )
}

// ─── Column Elements ─────────────────────────────────────────────────

export interface ElementColumnsProps {
  /** Column distribution pattern */
  pattern: string
  /** Gap class */
  gap?: string
  className?: string
  children?: React.ReactNode
}

/** Maps column patterns to CSS grid-template-columns */
export const COLUMN_PATTERNS: Record<string, string> = {
  '1': '1fr',
  '2': '1fr 1fr',
  '3': '1fr 1fr 1fr',
  '4': '1fr 1fr 1fr 1fr',
  '2:1': '2fr 1fr',
  '1:2': '1fr 2fr',
  '1:2:1': '1fr 2fr 1fr',
  '2:1:2': '2fr 1fr 2fr',
}

export function ElementColumns({ pattern = '2', gap = 'gap-6', className, children }: ElementColumnsProps) {
  const gridTemplate = COLUMN_PATTERNS[pattern] || COLUMN_PATTERNS['2']
  return (
    <div className={cn('grid w-full', gap, className)} style={{ gridTemplateColumns: gridTemplate }}>
      {children}
    </div>
  )
}

// ─── Text Elements ───────────────────────────────────────────────────

export interface ElementHeadingProps {
  /** Heading text */
  text?: string
  /** Heading level */
  level?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  /** Text alignment */
  align?: 'left' | 'center' | 'right'
  className?: string
}

const HEADING_STYLES: Record<string, string> = {
  h1: 'text-4xl md:text-5xl font-bold',
  h2: 'text-3xl md:text-4xl font-bold',
  h3: 'text-2xl md:text-3xl font-semibold',
  h4: 'text-xl md:text-2xl font-semibold',
  h5: 'text-lg md:text-xl font-medium',
  h6: 'text-base md:text-lg font-medium',
}

export function ElementHeading({ text = 'Heading', level = 'h2', align = 'left', className }: ElementHeadingProps) {
  const Tag = level
  const alignMap: Record<string, string> = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  }
  return <Tag className={cn(HEADING_STYLES[level], alignMap[align], className)}>{text}</Tag>
}

export interface ElementParagraphProps {
  /** Paragraph text */
  text?: string
  /** Text alignment */
  align?: 'left' | 'center' | 'right'
  /** Font size class */
  size?: 'sm' | 'base' | 'lg' | 'xl'
  className?: string
}

export function ElementParagraph({
  text = 'Enter your text here...',
  align = 'left',
  size = 'base',
  className,
}: ElementParagraphProps) {
  const sizeMap: Record<string, string> = {
    sm: 'text-sm',
    base: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl',
  }
  const alignMap: Record<string, string> = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  }
  return <p className={cn(sizeMap[size], alignMap[align], 'leading-relaxed text-gray-700', className)}>{text}</p>
}

// ─── Action Elements ─────────────────────────────────────────────────

export interface ElementButtonProps {
  /** Button text */
  text?: string
  /** Button URL */
  href?: string
  /** Variant */
  variant?: 'primary' | 'secondary' | 'outline'
  /** Size */
  size?: 'sm' | 'md' | 'lg'
  /** Full width */
  fullWidth?: boolean
  className?: string
}

export function ElementButton({
  text = 'Click me',
  href = '#',
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  className,
}: ElementButtonProps) {
  const variantStyles: Record<string, string> = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700 border-transparent',
    secondary: 'bg-gray-100 text-gray-900 hover:bg-gray-200 border-transparent',
    outline: 'bg-transparent text-blue-600 hover:bg-blue-50 border-blue-600',
  }
  const sizeStyles: Record<string, string> = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-5 py-2.5 text-base',
    lg: 'px-8 py-3.5 text-lg',
  }
  return (
    <a
      href={href}
      className={cn(
        'inline-flex items-center justify-center rounded-lg border font-semibold transition-colors',
        variantStyles[variant],
        sizeStyles[size],
        fullWidth && 'w-full',
        className
      )}
    >
      {text}
    </a>
  )
}

export interface ElementFormProps {
  /** Heading text */
  heading?: string
  /** Show name field */
  showName?: boolean
  /** Show email field */
  showEmail?: boolean
  /** Show message field */
  showMessage?: boolean
  /** Button text */
  buttonText?: string
  className?: string
}

export function ElementForm({
  heading = 'Contact Form',
  showName = true,
  showEmail = true,
  showMessage = true,
  buttonText = 'Submit',
  className,
}: ElementFormProps) {
  return (
    <div className={cn('rounded-xl border border-gray-200 bg-white p-6', className)}>
      {heading && <h3 className="mb-4 text-lg font-semibold">{heading}</h3>}
      <form className="space-y-4" onSubmit={e => e.preventDefault()}>
        {showName && (
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Name</label>
            <input
              type="text"
              placeholder="Your name"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
        )}
        {showEmail && (
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              placeholder="your@email.com"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
        )}
        {showMessage && (
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Message</label>
            <textarea
              placeholder="Your message..."
              rows={4}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
        )}
        <button
          type="submit"
          className="w-full rounded-lg bg-blue-600 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
        >
          {buttonText}
        </button>
      </form>
    </div>
  )
}

export interface ElementLinkProps {
  /** Link text */
  text?: string
  /** URL */
  href?: string
  /** Open in new tab */
  newTab?: boolean
  className?: string
}

export function ElementLink({ text = 'Link text', href = '#', newTab = false, className }: ElementLinkProps) {
  return (
    <a
      href={href}
      target={newTab ? '_blank' : undefined}
      rel={newTab ? 'noopener noreferrer' : undefined}
      className={cn('text-blue-600 underline hover:text-blue-800 transition-colors', className)}
    >
      {text}
    </a>
  )
}

// ─── Media Elements ──────────────────────────────────────────────────

export interface ElementImageProps {
  /** Image URL */
  src?: string
  /** Alt text */
  alt?: string
  /** Object fit */
  objectFit?: 'cover' | 'contain' | 'fill' | 'none'
  /** Border radius */
  rounded?: 'none' | 'md' | 'lg' | 'xl' | 'full'
  className?: string
}

export function ElementImage({
  src = '',
  alt = 'Image',
  objectFit = 'cover',
  rounded = 'md',
  className,
}: ElementImageProps) {
  const roundedMap: Record<string, string> = {
    none: 'rounded-none',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    full: 'rounded-full',
  }
  if (!src) {
    return (
      <div
        className={cn(
          'flex h-48 w-full items-center justify-center bg-gray-100 text-gray-400',
          roundedMap[rounded],
          className
        )}
      >
        <span className="text-sm">Select an image</span>
      </div>
    )
  }
  // eslint-disable-next-line @next/next/no-img-element
  return <img src={src} alt={alt} className={cn('w-full', roundedMap[rounded], className)} style={{ objectFit }} />
}

export interface ElementVideoProps {
  /** Video URL */
  src?: string
  /** Poster image */
  poster?: string
  /** Autoplay */
  autoplay?: boolean
  /** Controls */
  controls?: boolean
  className?: string
}

export function ElementVideo({ src = '', poster, autoplay = false, controls = true, className }: ElementVideoProps) {
  if (!src) {
    return (
      <div
        className={cn('flex h-48 w-full items-center justify-center rounded-lg bg-gray-100 text-gray-400', className)}
      >
        <span className="text-sm">Add a video URL</span>
      </div>
    )
  }
  return (
    <video
      src={src}
      poster={poster}
      autoPlay={autoplay}
      controls={controls}
      muted={autoplay}
      className={cn('w-full rounded-lg', className)}
    />
  )
}

export interface ElementEmbedProps {
  /** Embed URL (iframe src) */
  url?: string
  /** Height in pixels */
  height?: number
  className?: string
}

export function ElementEmbed({ url = '', height = 400, className }: ElementEmbedProps) {
  if (!url) {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 text-gray-400',
          className
        )}
        style={{ height }}
      >
        <span className="text-sm">Paste an embed URL</span>
      </div>
    )
  }
  return (
    <iframe
      src={url}
      className={cn('w-full rounded-lg border-0', className)}
      style={{ height }}
      allowFullScreen
      title="Embedded content"
    />
  )
}

// ─── Basic Elements ──────────────────────────────────────────────────

export interface ElementBadgeProps {
  /** Badge text */
  text?: string
  /** Color variant */
  color?: 'blue' | 'green' | 'red' | 'yellow' | 'purple' | 'gray'
  className?: string
}

const BADGE_COLORS: Record<string, string> = {
  blue: 'bg-blue-100 text-blue-700',
  green: 'bg-green-100 text-green-700',
  red: 'bg-red-100 text-red-700',
  yellow: 'bg-yellow-100 text-yellow-700',
  purple: 'bg-purple-100 text-purple-700',
  gray: 'bg-gray-100 text-gray-700',
}

export function ElementBadge({ text = 'Badge', color = 'blue', className }: ElementBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-3 py-1 text-xs font-medium',
        BADGE_COLORS[color],
        className
      )}
    >
      {text}
    </span>
  )
}

export interface ElementBadgeListProps {
  /** Badges */
  badges?: Array<{ text: string; color?: string }>
  /** Gap class */
  gap?: string
  className?: string
}

export function ElementBadgeList({
  badges = [
    { text: 'Badge 1', color: 'blue' },
    { text: 'Badge 2', color: 'green' },
    { text: 'Badge 3', color: 'purple' },
  ],
  gap = 'gap-2',
  className,
}: ElementBadgeListProps) {
  return (
    <div className={cn('flex flex-wrap', gap, className)}>
      {badges.map((badge, i) => (
        <ElementBadge key={i} text={badge.text} color={(badge.color as ElementBadgeProps['color']) || 'blue'} />
      ))}
    </div>
  )
}

export interface ElementBlockquoteProps {
  /** Quote text */
  text?: string
  /** Citation */
  citation?: string
  className?: string
}

export function ElementBlockquote({ text = 'This is a blockquote.', citation, className }: ElementBlockquoteProps) {
  return (
    <blockquote className={cn('border-l-4 border-blue-500 py-2 pl-4 italic text-gray-600', className)}>
      <p>{text}</p>
      {citation && <cite className="mt-2 block text-sm not-italic text-gray-500">— {citation}</cite>}
    </blockquote>
  )
}

export interface ElementCodeProps {
  /** Code content */
  code?: string
  /** Language label */
  language?: string
  className?: string
}

export function ElementCode({
  code = 'console.log("Hello World")',
  language = 'javascript',
  className,
}: ElementCodeProps) {
  return (
    <div className={cn('overflow-hidden rounded-lg bg-gray-900', className)}>
      {language && (
        <div className="flex items-center justify-between bg-gray-800 px-4 py-2 text-xs text-gray-400">
          <span>{language}</span>
        </div>
      )}
      <pre className="overflow-x-auto p-4">
        <code className="text-sm text-gray-100">{code}</code>
      </pre>
    </div>
  )
}

export interface ElementCustomHTMLProps {
  /** Raw HTML content */
  html?: string
  className?: string
}

export function ElementCustomHTML({ html = '<p>Custom HTML content</p>', className }: ElementCustomHTMLProps) {
  return (
    <div className={cn('prose max-w-none', className)} dangerouslySetInnerHTML={{ __html: sanitizeHtmlValue(html) }} />
  )
}

export function ElementLine({ className }: { className?: string }) {
  return <hr className={cn('border-t border-gray-200 my-4', className)} />
}

export interface ElementListProps {
  /** List items */
  items?: string[]
  /** List style */
  ordered?: boolean
  className?: string
}

export function ElementList({
  items = ['List item 1', 'List item 2', 'List item 3'],
  ordered = false,
  className,
}: ElementListProps) {
  const Tag = ordered ? 'ol' : 'ul'
  return (
    <Tag className={cn(ordered ? 'list-decimal' : 'list-disc', 'space-y-1 pl-5 text-gray-700', className)}>
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </Tag>
  )
}
